import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { handleAPIError, logError } from "@/lib/errorHandler";
import { fetchJSON } from "@/lib/api";
import { fetchEmployees } from "@/lib/employees";
import { formatDate, formatDateTime } from "@/lib/dateUtils";
import { Plus, Trash2, Search, Edit, Printer, FileText, Send, X } from "lucide-react";

const formSchema = z.object({
  expense_date: z.string().min(1, "Date is required"),
  expense_type_id: z.string().min(1, "Select a valid type"), // changed from uuid check to min(1) as mongo ids might vary or be uuid strings
  flow_type: z.enum(["Inflow", "Outflow"]).default("Outflow"),
  payment_mode: z.enum(["Cash", "Bank", "UPI"]).default("Cash"),
  amount: z.string().min(1, "Amount is required"),
  description: z.string().optional(),
  shift: z.enum(["S-1", "S-2"]).optional(),
  employee_id: z.string().optional(),
  paid_to: z.string().optional(),
  effect_asset: z.string().optional(), // 'Yes' or 'No'
});

type FormValues = z.infer<typeof formSchema>;

export default function Expenses() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [newTypeName, setNewTypeName] = useState("");
  const [newTypeEffect, setNewTypeEffect] = useState("No"); // Default to 'No' (unchecked)
  const [newTypeOption, setNewTypeOption] = useState("Benefit");
  const [isAddTypeOpen, setIsAddTypeOpen] = useState(false);

  // States to mimic design
  const [searchFrom, setSearchFrom] = useState("");
  const [searchTo, setSearchTo] = useState("");
  const [searchFlow, setSearchFlow] = useState("");
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [editingId, setEditingId] = useState<string | null>(null);

  // Queries
  const { data: types } = useQuery({
    queryKey: ["/api/expense-types"],
    queryFn: async () => (await fetchJSON('/api/expense-types')).json().then(res => res.rows || [])
  });

  const { data: employees } = useQuery({
    queryKey: ["/api/employees"],
    queryFn: fetchEmployees,
  });

  useEffect(() => {
    if (employees) {
      console.log("Employees loaded:", employees);
      // toast({ title: "Debug", description: `Loaded ${employees.length} employees` }); 
    }
  }, [employees]);

  const { data: recentExpenses, isLoading: loadingList, refetch: refetchExpenses } = useQuery({
    queryKey: ["/api/expenses"],
    queryFn: async () => {
      let url = '/api/expenses?';
      if (searchFrom && searchTo) url += `from_date=${searchFrom}&to_date=${searchTo}&`;
      if (searchFlow) url += `flow_type=${searchFlow}`;
      return (await fetchJSON(url)).json().then(res => res.rows || [])
    },
  });

  const [filterText, setFilterText] = useState("");

  const filteredExpenses = useMemo(() => {
    const expenses = recentExpenses || [];
    if (!filterText) return expenses;
    const lowerSearch = filterText.toLowerCase();
    return expenses.filter((row: any) => {
      const dateStr = row.expense_date ? formatDate(row.expense_date).toLowerCase() : '';
      const shiftName = (row.shift || '').toLowerCase();
      const empName = (row.employee_name || '').toLowerCase();
      const expType = (row.expense_type || '').toLowerCase();
      const flowType = (row.flow_type || '').toLowerCase();
      const payMode = (row.payment_mode || '').toLowerCase();
      const amountStr = (row.amount || '').toString().toLowerCase();
      const paidTo = (row.paid_to || '').toLowerCase();
      const effectAsset = (row.effect_asset || '').toLowerCase();
      const desc = (row.description || '').toLowerCase();

      return (
        dateStr.includes(lowerSearch) ||
        shiftName.includes(lowerSearch) ||
        empName.includes(lowerSearch) ||
        expType.includes(lowerSearch) ||
        flowType.includes(lowerSearch) ||
        payMode.includes(lowerSearch) ||
        amountStr.includes(lowerSearch) ||
        paidTo.includes(lowerSearch) ||
        effectAsset.includes(lowerSearch) ||
        desc.includes(lowerSearch)
      );
    });
  }, [recentExpenses, filterText]);

  const defaultDate = useMemo(() => new Date().toISOString().split("T")[0], []);

  const [shift, setShift] = useState<"S-1" | "S-2">("S-1");
  const [effectAsset, setEffectAsset] = useState<"Yes" | "No">("No");

  const { register, handleSubmit, setValue, reset, watch, formState: { errors, isSubmitting } } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      expense_date: defaultDate,
      flow_type: "Outflow",
      payment_mode: "Cash",
      shift: "S-1",
      effect_asset: "No",
      amount: ""
    },
  });

  useEffect(() => {
    setValue("expense_date", defaultDate);
  }, [defaultDate, setValue]);

  // Handler for Add Type
  const handleAddType = async () => {
    if (!newTypeName) {
      toast({ title: "Error", description: "Expense Category is required", variant: "destructive" });
      return;
    }
    try {
      const res = await fetchJSON('/api/expense-types', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          expense_type_name: newTypeName,
          effect_type: newTypeEffect,
          option: newTypeOption
        })
      });
      const result = await res.json();
      if (!result.ok) throw new Error(result.error);

      toast({ title: "Success", description: "Expense Type Added" });
      setNewTypeName("");
      setNewTypeEffect("No");
      setNewTypeOption("Benefit");
      setIsAddTypeOpen(false);
      qc.invalidateQueries({ queryKey: ["/api/expense-types"] });
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
  };

  const onSubmit = async (values: FormValues) => {
    const amountNumber = Number(values.amount);
    if (Number.isNaN(amountNumber) || amountNumber <= 0) {
      toast({ title: "Invalid amount", description: "Enter a positive amount", variant: "destructive" });
      return;
    }

    const payload = {
      expense_date: values.expense_date,
      expense_type_id: values.expense_type_id,
      flow_type: values.flow_type,
      payment_mode: values.payment_mode,
      amount: amountNumber,
      description: values.description || "",
      shift: shift,
      employee_id: values.employee_id || undefined,
      paid_to: values.paid_to || "",
      effect_asset: effectAsset,
    };

    try {
      const isEdit = Boolean(editingId);
      const response = await fetchJSON(isEdit ? `/api/expenses/${editingId}` : '/api/expenses', {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const result = await response.json();

      if (!result.ok) {
        toast({ title: "Error", description: result.error, variant: "destructive" });
        return;
      }

      toast({ title: isEdit ? "Expense updated" : "Expense added" });
      setValue("amount", "");
      setValue("description", "");
      setValue("paid_to", "");
      if (isEdit) {
        setEditingId(null);
      }
      refetchExpenses();
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  };

  const handleEditRow = (row: any) => {
    setEditingId(String(row.id));
    const dateStr = row.expense_date
      ? new Date(row.expense_date).toISOString().split("T")[0]
      : defaultDate;
    setValue("expense_date", dateStr);
    setValue("expense_type_id", row.expense_type_id || "");
    setValue("flow_type", row.flow_type || "Outflow");
    setValue("payment_mode", row.payment_mode || "Cash");
    setValue("amount", String(row.amount ?? ""));
    setValue("description", row.description || "");
    setValue("employee_id", row.employee_id || "");
    setValue("paid_to", row.paid_to || "");
    setShift(row.shift === "S-2" ? "S-2" : "S-1");
    const effect = row.effect_asset === "Yes" ? "Yes" : "No";
    setEffectAsset(effect);
    setValue("effect_asset", effect);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelEdit = () => {
    setEditingId(null);
    reset({
      expense_date: defaultDate,
      flow_type: "Outflow",
      payment_mode: "Cash",
      shift: "S-1",
      effect_asset: "No",
      amount: "",
    });
    setShift("S-1");
    setEffectAsset("No");
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure?")) return;
    await fetchJSON(`/api/expenses/${id}`, { method: 'DELETE' });
    refetchExpenses();
  };

  const handleBulkDelete = async () => {
    if (selectedRows.size === 0) {
      toast({ title: "Nothing selected", description: "Select expenses to delete", variant: "destructive" });
      return;
    }
    if (!confirm(`Delete ${selectedRows.size} expense(s)?`)) return;
    for (const id of selectedRows) {
      await fetchJSON(`/api/expenses/${id}`, { method: 'DELETE' });
    }
    setSelectedRows(new Set());
    refetchExpenses();
    toast({ title: "Deleted", description: "Selected expenses removed" });
  };

  const toggleRowSelection = (id: string, checked: boolean) => {
    setSelectedRows((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const toggleAllRows = (checked: boolean) => {
    if (!checked) {
      setSelectedRows(new Set());
      return;
    }
    setSelectedRows(new Set(filteredExpenses.map((row: any) => String(row.id))));
  };

  // Multi-row state
  const blankRow = () => ({
    id: Date.now().toString() + Math.random(),
    employee_id: "",
    expense_type_id: "",
    flow_type: "Outflow" as "Inflow" | "Outflow",
    payment_mode: "Cash" as "Cash" | "Bank" | "UPI",
    amount: "",
    effect_asset: "No" as "Yes" | "No",
    description: "",
    paid_to: "",
  });

  const [expenseItems, setExpenseItems] = useState([blankRow()]);
  const [autoFillEmpId, setAutoFillEmpId] = useState("");
  const [autoFillTypeId, setAutoFillTypeId] = useState("");

  const updateItem = (id: string, field: string, value: string) =>
    setExpenseItems(prev => prev.map(r => r.id === id ? { ...r, [field]: value } : r));

  const addRow = () => setExpenseItems(prev => [...prev, blankRow()]);
  const removeRow = (id: string) => setExpenseItems(prev => prev.length > 1 ? prev.filter(r => r.id !== id) : prev);

  const handleAutoFillEmpMulti = (val: string) => {
    setAutoFillEmpId(val);
    setExpenseItems(prev => prev.map(r => ({ ...r, employee_id: val })));
  };
  const handleAutoFillTypeMulti = (val: string) => {
    setAutoFillTypeId(val);
    setExpenseItems(prev => prev.map(r => ({ ...r, expense_type_id: val })));
  };

  const handleConfirm = async () => {
    const expDate = watch("expense_date");
    for (const item of expenseItems) {
      const amt = Number(item.amount);
      if (!item.expense_type_id || isNaN(amt) || amt <= 0) {
        toast({ title: "Missing fields", description: "Fill Expense Type and Amount for all rows", variant: "destructive" });
        return;
      }
    }
    try {
      for (const item of expenseItems) {
        const payload = {
          expense_date: expDate,
          expense_type_id: item.expense_type_id,
          flow_type: item.flow_type,
          payment_mode: item.payment_mode,
          amount: Number(item.amount),
          description: item.description || "",
          shift,
          employee_id: item.employee_id || undefined,
          paid_to: item.paid_to || "",
          effect_asset: item.effect_asset,
        };
        const res = await fetchJSON('/api/expenses', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        const result = await res.json();
        if (!result.ok) throw new Error(result.error);
      }
      toast({ title: "Success", description: `${expenseItems.length} expense(s) saved!` });
      setExpenseItems([blankRow()]);
      setAutoFillEmpId("");
      setAutoFillTypeId("");
      refetchExpenses();
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 p-2 font-sans text-sm">

      {/* Main Card */}
      <Card className="bg-[#3b66f5] text-white border-none shadow-lg mb-4 rounded-xl overflow-hidden">
        <div className="p-4">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold">Expenses</h2>
            <Button
              size="sm"
              className="bg-white text-blue-600 hover:bg-gray-100 rounded-full font-semibold"
              onClick={() => setIsAddTypeOpen(true)}
            >
              Add Expense Type <Plus className="w-4 h-4 ml-1" />
            </Button>
          </div>

          {/* Top Stats Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold uppercase opacity-80 w-24">Total Outflow</span>
              <div className="h-9 bg-white text-black rounded flex items-center px-3 font-medium flex-1">Cash Out</div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold uppercase opacity-80 w-24">Total Inflow</span>
              <div className="h-9 bg-white text-black rounded flex items-center px-3 font-medium flex-1">Cash In</div>
            </div>
          </div>

          {/* Date & Shift Row */}
          <div className="flex items-center gap-2 mb-6">
            <div className="bg-[#fbbf24] text-black font-bold px-4 py-2 rounded-l-md whitespace-nowrap h-10 flex items-center">Choose Date</div>
            <Input
              type="date"
              {...register("expense_date")}
              className="w-40 h-10 bg-white text-black rounded-none rounded-r-md border-none"
            />

            <div className="flex-1 bg-transparent border border-white/30 rounded-full px-4 py-2 flex items-center justify-center gap-6 ml-4 h-10">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="shift" checked={shift === 'S-1'} onChange={() => setShift('S-1')} className="accent-white w-4 h-4" />
                <span className="font-bold">S-1</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="shift" checked={shift === 'S-2'} onChange={() => setShift('S-2')} className="accent-white w-4 h-4" />
                <span className="font-bold">S-2</span>
              </label>
            </div>
          </div>

          {/* Auto Fill Section */}
          <div className="border border-white/50 rounded-lg p-3 mb-6 relative mt-4">
            <span className="absolute -top-3 left-4 bg-[#3b66f5] px-2 text-xs font-bold uppercase">AUTO-FILL</span>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs mb-1 block">Employee</Label>
                <Select value={autoFillEmpId} onValueChange={handleAutoFillEmpMulti}>
                  <SelectTrigger className="bg-white text-black h-9 border-none"><SelectValue placeholder="choose Employee" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="placeholder" disabled>Select Employee</SelectItem>
                    {employees?.map((e: any) => <SelectItem key={e.id} value={e.id}>{e.employee_name || "Unknown"}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs mb-1 block">Expenses Type</Label>
                <Select value={autoFillTypeId} onValueChange={handleAutoFillTypeMulti}>
                  <SelectTrigger className="bg-white text-black h-9 border-none"><SelectValue placeholder="Choose Type" /></SelectTrigger>
                  <SelectContent>{types?.map((t: any) => <SelectItem key={t.id} value={t.id}>{t.expense_type_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Multi-row Input */}
          <div className="space-y-3 mb-4">
            {expenseItems.map((item, index) => (
              <div key={item.id} className="relative border border-white/20 p-3 rounded-xl bg-blue-700/30">
                <div className="grid grid-cols-1 md:grid-cols-[2fr_2fr_1fr_1fr_1fr_1fr_2fr_auto] gap-2 items-end">
                  {/* Employee */}
                  <div>
                    <Label className="text-[10px] text-white block mb-1">Employee</Label>
                    <Select value={item.employee_id} onValueChange={v => updateItem(item.id, 'employee_id', v)}>
                      <SelectTrigger className="bg-white text-black h-9 border-none"><SelectValue placeholder="Employee" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="placeholder" disabled>Select Employee</SelectItem>
                        {employees?.map((e: any) => <SelectItem key={e.id} value={e.id}>{e.employee_name || "Unknown"}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  {/* Expense Type */}
                  <div>
                    <Label className="text-[10px] text-white block mb-1">Expense Type</Label>
                    <Select value={item.expense_type_id} onValueChange={v => updateItem(item.id, 'expense_type_id', v)}>
                      <SelectTrigger className="bg-white text-black h-9 border-none"><SelectValue placeholder="Expense Type" /></SelectTrigger>
                      <SelectContent>{types?.map((t: any) => <SelectItem key={t.id} value={t.id}>{t.expense_type_name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  {/* Flow */}
                  <div>
                    <Label className="text-[10px] text-white block text-center mb-1">Flow</Label>
                    <Select value={item.flow_type} onValueChange={v => updateItem(item.id, 'flow_type', v)}>
                      <SelectTrigger className="bg-white text-black h-9 border-none"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="Outflow">Outflow</SelectItem><SelectItem value="Inflow">Inflow</SelectItem></SelectContent>
                    </Select>
                  </div>
                  {/* Amount */}
                  <div>
                    <Label className="text-[10px] text-white block text-center mb-1">Amount</Label>
                    <Input placeholder="0.00" type="number" value={item.amount} onChange={e => updateItem(item.id, 'amount', e.target.value)} className="bg-white text-black h-9 border-none font-bold" />
                  </div>
                  {/* Mode */}
                  <div>
                    <Label className="text-[10px] text-white block text-center mb-1">Mode</Label>
                    <Select value={item.payment_mode} onValueChange={v => updateItem(item.id, 'payment_mode', v)}>
                      <SelectTrigger className="bg-white text-black h-9 border-none"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="Cash">Cash</SelectItem><SelectItem value="Bank">Bank</SelectItem><SelectItem value="UPI">UPI</SelectItem></SelectContent>
                    </Select>
                  </div>
                  {/* Effect */}
                  <div>
                    <Label className="text-[10px] text-white block text-center mb-1">Effect</Label>
                    <Select value={item.effect_asset} onValueChange={v => updateItem(item.id, 'effect_asset', v)}>
                      <SelectTrigger className="bg-white text-black h-9 border-none"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="Yes">Yes</SelectItem><SelectItem value="No">No</SelectItem></SelectContent>
                    </Select>
                  </div>
                  {/* Note */}
                  <div>
                    <Label className="text-[10px] text-white block mb-1">Note 📂</Label>
                    <Input placeholder="Description" value={item.description} onChange={e => updateItem(item.id, 'description', e.target.value)} className="bg-white text-black h-9 border-none" />
                  </div>
                  {/* + / × buttons */}
                  <div className="flex gap-2 pb-[2px] items-end">
                    {!editingId && (
                      <Button
                        type="button"
                        size="icon"
                        className="bg-transparent border border-white text-white hover:bg-white/20 rounded-full h-9 w-9 shrink-0"
                        onClick={addRow}
                        title="Add row"
                      >
                        <Plus className="h-5 w-5" />
                      </Button>
                    )}
                    {expenseItems.length > 1 && !editingId && (
                      <Button
                        type="button"
                        size="icon"
                        className="bg-red-500 hover:bg-red-600 border-none text-white rounded-full h-9 w-9 shrink-0"
                        onClick={() => removeRow(item.id)}
                        title="Remove row"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Confirm / Cancel */}
          <div className="flex justify-center gap-3 mt-4">
            {editingId && (
              <Button type="button" variant="outline" onClick={cancelEdit} className="bg-white/10 text-white border-white/50 hover:bg-white/20 rounded-full px-8">
                Cancel
              </Button>
            )}
            <Button onClick={handleConfirm} className="bg-[#84cc16] hover:bg-[#65a30d] text-white font-bold px-12 rounded-full shadow-lg">
              {editingId ? "UPDATE" : "CONFIRM"}
            </Button>
          </div>

        </div>
      </Card>


      {/* Filter Bar */}
      <div className="bg-white p-3 rounded-lg shadow-sm mb-4 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-gray-600">Search From</span>
          <Input type="date" value={searchFrom} onChange={e => setSearchFrom(e.target.value)} className="w-32 h-8" />
        </div>
        <div className="flex items-center gap-2">
          <span className="font-semibold text-gray-600">To</span>
          <Input type="date" value={searchTo} onChange={e => setSearchTo(e.target.value)} className="w-32 h-8" />
        </div>
        <div className="flex items-center gap-2 flex-1">
          <span className="font-semibold text-gray-600">Flow Type</span>
          <Select value={searchFlow} onValueChange={setSearchFlow}>
            <SelectTrigger className="w-48 h-8"><SelectValue placeholder="Choose Flow" /></SelectTrigger>
            <SelectContent><SelectItem value="Outflow">Outflow</SelectItem><SelectItem value="Inflow">Inflow</SelectItem></SelectContent>
          </Select>
          <Button onClick={() => refetchExpenses()} className="bg-orange-500 hover:bg-orange-600 text-white h-8 px-4"><Search className="w-4 h-4 mr-1" /> Search</Button>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-2 mb-2">
        <Button variant="outline" className="text-red-500 border-red-200 bg-red-50 h-8" onClick={handleBulkDelete}><Trash2 className="w-4 h-4 mr-1" /> Delete</Button>
        <Button variant="outline" className="text-blue-600 border-blue-200 bg-blue-50 h-8"><Printer className="w-4 h-4 mr-1" /> Direct Print</Button>

        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-gray-500">Filter:</span>
          <Input placeholder="Type to filter..." className="h-8 w-48" value={filterText} onChange={e => setFilterText(e.target.value)} />
        </div>
        <div className="flex items-center gap-2 text-sm">
          <span className="text-gray-500">Show:</span>
          <Select defaultValue="all">
            <SelectTrigger className="w-24 h-7"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All</SelectItem></SelectContent>
          </Select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="w-full text-xs text-left border-collapse">
          <thead className="bg-gray-50 text-gray-700 font-semibold border-b">
            <tr>
              <th className="p-2 border-r">
                <input
                  type="checkbox"
                  checked={filteredExpenses.length > 0 && selectedRows.size === filteredExpenses.length}
                  onChange={(e) => toggleAllRows(e.target.checked)}
                />
              </th>
              <th className="p-2 border-r">S.No</th>
              <th className="p-2 border-r">Date</th>
              <th className="p-2 border-r">Shift</th>
              <th className="p-2 border-r">Employee</th>
              <th className="p-2 border-r">Expenses Type</th>
              <th className="p-2 border-r">Flow</th>
              <th className="p-2 border-r">Mode</th>
              <th className="p-2 border-r">Amount</th>
              <th className="p-2 border-r">Paid To</th>
              <th className="p-2 border-r">Effect Asset</th>
              <th className="p-2 border-r">Description</th>
              <th className="p-2 border-r text-center">Action</th>
              <th className="p-2 border-r">Picture</th>
              <th className="p-2 border-r">User Log Details</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {filteredExpenses.map((row: any, i: number) => (
              <tr key={row.id} className="hover:bg-gray-50">
                <td className="p-2 border-r text-center">
                  <input
                    type="checkbox"
                    checked={selectedRows.has(String(row.id))}
                    onChange={(e) => toggleRowSelection(String(row.id), e.target.checked)}
                  />
                </td>
                <td className="p-2 border-r text-center">{i + 1}</td>
                <td className="p-2 border-r whitespace-nowrap">{formatDate(row.expense_date)}</td>
                <td className="p-2 border-r text-center">{row.shift}</td>
                <td className="p-2 border-r">{row.employee_name}</td>
                <td className="p-2 border-r font-medium">{row.expense_type}</td>
                <td className="p-2 border-r">{row.flow_type}</td>
                <td className="p-2 border-r">{row.payment_mode}</td>
                <td className="p-2 border-r font-bold">{Number(row.amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                <td className="p-2 border-r">{row.paid_to || '-'}</td>
                <td className="p-2 border-r">{row.effect_asset || 'No'}</td>
                <td className="p-2 border-r">{row.description}</td>
                <td className="p-2 border-r flex justify-center gap-2">
                  <Button size="icon" variant="ghost" className="h-6 w-6 text-blue-500" onClick={() => handleEditRow(row)}><Edit className="w-3 h-3" /></Button>
                  <Button size="icon" variant="ghost" className="h-6 w-6 text-red-500" onClick={() => handleDelete(row.id)}><Trash2 className="w-3 h-3" /></Button>
                </td>
                <td className="p-2 border-r text-center">-</td>
                <td className="p-2 border-r text-gray-400 text-[10px]">Created: {row.created_at || row.createdAt ? formatDateTime(row.created_at || row.createdAt) : '-'}</td>
              </tr>
            ))}
            {filteredExpenses.length === 0 && (
              <tr><td colSpan={15} className="p-8 text-center text-gray-500">No expenses found.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Add Type Dialog */}
      <Dialog open={isAddTypeOpen} onOpenChange={setIsAddTypeOpen}>
        <DialogContent className="sm:max-w-4xl p-0 overflow-hidden">
          <DialogHeader className="bg-[#0056b3] p-4 text-white flex flex-row justify-between items-center space-y-0">
            <DialogTitle className="text-lg">Add Expense Type</DialogTitle>
            <DialogClose className="text-white hover:bg-white/20 rounded-sm opacity-100 transition-opacity">
              <X className="h-5 w-5" />
              <span className="sr-only">Close</span>
            </DialogClose>
          </DialogHeader>

          <div className="p-8 bg-white min-h-[200px] flex items-center">
            <div className="grid grid-cols-12 gap-6 w-full items-start">
              {/* Expense Category */}
              <div className="col-span-4 space-y-2">
                <Input
                  placeholder="Expense Category"
                  className="h-10 text-sm border-gray-300"
                  value={newTypeName}
                  onChange={(e) => setNewTypeName(e.target.value)}
                />
              </div>

              {/* Effect Employee Checkbox */}
              <div className="col-span-4 flex items-center justify-center h-10 gap-3">
                <div
                  className={`w-8 h-8 rounded border flex items-center justify-center cursor-pointer transition-colors ${newTypeEffect === "Yes" ? "bg-[#007bff] border-[#007bff]" : "border-gray-300 bg-white"}`}
                  onClick={() => setNewTypeEffect(newTypeEffect === "Yes" ? "No" : "Yes")}
                >
                  {newTypeEffect === "Yes" && <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                </div>
                <Label className="text-base font-bold text-black cursor-pointer" onClick={() => setNewTypeEffect(newTypeEffect === "Yes" ? "No" : "Yes")}>Effect Employee</Label>
              </div>

              {/* Select Option */}
              <div className="col-span-4 space-y-2 relative">
                <Select value={newTypeOption} onValueChange={setNewTypeOption}>
                  <SelectTrigger className="h-10 border-gray-300 relative">
                    <SelectValue placeholder="Select Option" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Benefit">Benefit</SelectItem>
                    <SelectItem value="Loan">Loan</SelectItem>
                    <SelectItem value="Penalty">Penalty</SelectItem>
                    <SelectItem value="Salary">Salary</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
                <span className="absolute -top-5 left-1 text-xs text-gray-500 font-semibold uppercase tracking-wider">Select Option</span>
              </div>
            </div>
          </div>

          <DialogFooter className="p-4 bg-white flex justify-center gap-4 sm:justify-center border-t-0 pb-8">
            <Button
              onClick={handleAddType}
              className="bg-green-500 hover:bg-green-600 text-white font-bold px-6 h-10 w-24"
            >
              ✔ Add
            </Button>
            <Button
              variant="destructive"
              onClick={() => setIsAddTypeOpen(false)}
              className="bg-red-500 hover:bg-red-600 text-white font-bold px-6 h-10 w-24"
            >
              ✖ Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

